import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSeedDensityRecommendation } from './api';
import { SeedDensityRecommendationInput, Recommendation } from '../../types';
import {
  POLLING_INTERVAL,
  SEED_DENSITY_RECOMMENDATION_QUERY_KEY,
  SEED_DENSITY_RECOMMENDATION_DRAFT_QUERY_KEY
} from '../../constants';
import { getEconomicRecommendation } from './utils';

export interface UseSeedDensityRecommendationParams {
  fieldId: string;
  onSuccess?: (data: Recommendation) => void;
  onError?: () => void;
  draft?: boolean;
}

export interface EconomicParameters {
  grainPrice: number;
  cropNominalWeight: number;
  seedsPerBag: number;
  seedCostPerBag: number;
}

interface RecommendationParams {
  input: SeedDensityRecommendationInput;
  economicParameters: EconomicParameters;
}

export const useSeedDensityRecommendation = ({
  fieldId,
  onSuccess,
  onError,
  draft = false,
}: UseSeedDensityRecommendationParams) => {
  const [recommendationParams, setRecommendationParams] = useState<RecommendationParams | null>(null);
  const { input, economicParameters } = recommendationParams || {};

  const { data: recommendation = null, error, isFetching } = useQuery({
    queryKey: [draft
      ? SEED_DENSITY_RECOMMENDATION_DRAFT_QUERY_KEY
      : SEED_DENSITY_RECOMMENDATION_QUERY_KEY, fieldId, input
    ],
    queryFn: async () => {
      const { seedingRateRecommendation } = await fetchSeedDensityRecommendation(input);

      if (!seedingRateRecommendation?.curve) {
        return null;
      }

      const economicRecommendation = getEconomicRecommendation({
        curve: seedingRateRecommendation.curve,
        economicParameters
      });

      return {
        seedingRateRecommendation: {
          ...seedingRateRecommendation,
          recommendation: economicRecommendation,
        },
      };
    },
    enabled: !!input,
    refetchInterval: (data) => {
      if (!data?.seedingRateRecommendation) {
        return POLLING_INTERVAL;
      }
      return false;
    },
    onSuccess: (data) => {
      if (data?.seedingRateRecommendation) {
        onSuccess?.(data.seedingRateRecommendation);
        setRecommendationParams(null);
      }
    },
    onError: () => {
      onError?.();
      setRecommendationParams(null);
    }
  });

  const isCachedData = useMemo(
    () => !!recommendation?.seedingRateRecommendation && !isFetching,
    [recommendation, isFetching]
  );

  const generateRecommendation = useCallback(
    (newInput: SeedDensityRecommendationInput, newEconomicParameters: EconomicParameters) => {
      setRecommendationParams({ input: newInput, economicParameters: newEconomicParameters });
    }, []);

  return {
    generateRecommendation,
    recommendation,
    isLoading: !!input && !isCachedData,
    error,
  };
};
